// `/financeiro-aberto`: para onde vai a empresa com licença vencida (menu
// escondido). Abre direto o modal de checkout — que lê a fatura aberta de
// `GET /subscriptions/checkout` — e deixa um cartão com o vencimento e o
// botão para reabrir. O checkout é só de admin (a rota responde 403 aos
// demais), então para outros perfis o modal não abre sozinho.
import React, { useCallback, useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";

import SubscriptionModal from "../../components/SubscriptionModal";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  card: {
    width: "100%",
    maxWidth: 480,
    boxSizing: "border-box",
    backgroundColor: theme.palette.aeros.surface,
    border: `1px solid ${theme.palette.type === "light" ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
    borderRadius: 14,
    padding: "18px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  field: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.aeros.textTertiary,
  },
  value: { fontSize: 14, fontWeight: 600, overflowWrap: "anywhere" },
  button: { minHeight: 44, borderRadius: 10, fontWeight: 700 },
  notice: {
    fontSize: 13,
    lineHeight: 1.5,
    padding: "10px 12px",
    borderRadius: 10,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.type === "light" ? "rgba(245,158,11,0.10)" : "rgba(245,158,11,0.12)",
    border: "1px solid rgba(245,158,11,0.35)",
  },
}));

const Subscription = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { returnDays } = useDate();
  const isAdmin = user?.profile === "admin";
  const [modalOpen, setModalOpen] = useState(false);

  const handleClose = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    if (isAdmin) setModalOpen(true);
  }, [isAdmin]);

  const days = user?.company?.dueDate ? returnDays(user.company.dueDate) : null;
  const licenseText =
    days === null
      ? "—"
      : days < 0
      ? i18n.t("subscription.checkout.licenseExpired")
      : days === 0
      ? i18n.t("subscription.checkout.licenseDueToday")
      : i18n.t("subscription.checkout.licenseDueIn", { days });

  return (
    <MainContainer>
      <SubscriptionModal open={modalOpen} onClose={handleClose} />

      <MainHeader>
        <Title>{i18n.t("subscription.checkout.pageTitle")}</Title>
      </MainHeader>

      <div className={classes.card}>
        <div className={classes.field}>
          <span className={classes.label}>
            {i18n.t("subscription.checkout.licenseLabel")}
          </span>
          <span className={classes.value}>{licenseText}</span>
        </div>
        <div className={classes.field}>
          <span className={classes.label}>
            {i18n.t("subscription.checkout.billingEmail")}
          </span>
          <span className={classes.value}>{user?.email || "—"}</span>
        </div>
        {!isAdmin && (
          // Checkout é só do admin (a rota responde 403 aos demais): sem este
          // aviso, atendente e supervisor ficavam numa tela sem nenhuma ação.
          <div role="note" className={classes.notice}>
            {i18n.t("subscription.checkout.adminOnlyNotice")}
          </div>
        )}
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className={classes.button}
            onClick={() => setModalOpen(true)}
          >
            {i18n.t("subscription.checkout.subscribeNow")}
          </Button>
        )}
      </div>
    </MainContainer>
  );
};

export default Subscription;
